import { AsyncPipe, SlicePipe } from '@angular/common';
import { AfterViewChecked, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import {
    ActivatedRoute,
    ActivationStart,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    Params,
    Router,
    RouterOutlet,
    Scroll
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import {
    ButtonComponent,
    ListComponent,
    ListItem,
    LoaderComponent,
    LogoComponent,
    SfxCommonsLocalizerService
} from '@swissfex/sfx-commons';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    DATE_FORMATS_PER_LANGUAGE,
    DATE_MONTH_FORMATS_PER_LANGUAGE,
    LANGUAGE_FILE_NAME_SUFFIX,
    SafeAny
} from './constants';
import { IDiagnosticLog } from './core/models/IDiagnosticLog';
import { DomUpdateSchedulerService } from './core/services/dom-update-scheduler.service';
import { LocationUtils } from './core/utils/location.utils';
import { ObjectUtils } from './core/utils/object.utils';
import { RouterUtils } from './core/utils/router.utils';
import { getItemsWithIndexAsId } from './core/utils/utils';
import { DiagnosticsService } from './diagnostics/services/diagnostics.service';
import { MadTranslateService } from './mad-translate/services/mad-translate.service';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        RouterOutlet,
        AsyncPipe,
        SlicePipe,
        LogoComponent,
        LoaderComponent,
        ListComponent,
        ButtonComponent,
        TranslatePipe
    ]
})
export class AppComponent implements OnInit, AfterViewChecked {
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly diagnosticsSvc = inject(DiagnosticsService);
    private readonly domUpdateSchedulerSvc = inject(DomUpdateSchedulerService);
    private readonly madTranslateSvc = inject(MadTranslateService);
    private readonly router = inject(Router);
    private readonly sfxCommonsLocalizerSvc = inject(SfxCommonsLocalizerService);

    public readonly diagnosticLogsListItems$: Observable<ListItem<IDiagnosticLog>[]>;
    public areDiagnosticsEnabled = signal(false);
    public isAppLoading = signal(true);
    public isModernBrowser = signal(true);

    private readonly diagnosticsQueryParamName = 'sfxdiag';

    constructor() {
        const { queryParams } = LocationUtils.parseQueryString() || {};
        this.areDiagnosticsEnabled.set(
            this.diagnosticsSvc.tryActivateService(queryParams?.[this.diagnosticsQueryParamName])
        );

        this.diagnosticLogsListItems$ = this.diagnosticsSvc.logs.pipe(map(getItemsWithIndexAsId<IDiagnosticLog>));

        this.activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((params: Params) => {
            if (params[this.diagnosticsQueryParamName]) {
                this.areDiagnosticsEnabled.set(
                    this.diagnosticsSvc.tryActivateService(params[this.diagnosticsQueryParamName])
                );
            }
        });

        window.onpopstate = async () => {
            const oldLanguage = this.router.url.split('/')[1];
            const newLanguage = window.location.pathname.split('/')[1];
            if (oldLanguage !== newLanguage) {
                await this.madTranslateSvc.setSelectedLanguage(
                    ObjectUtils.getKeyForValue(LANGUAGE_FILE_NAME_SUFFIX, newLanguage)
                );
            }
        };

        let routePaths = [];
        let routeComponents = {};
        this.router.events.pipe(untilDestroyed(this)).subscribe((event): void => {
            switch (true) {
                case event instanceof ActivationStart:
                    // eslint-disable-next-line no-case-declarations
                    const eventDup = event as SafeAny;
                    if (eventDup?.snapshot?.routeConfig?.component) {
                        const lastActivatedComponentName = eventDup.snapshot.routeConfig.component.name;
                        const lastActivatedRoute = RouterUtils.getFullPathFromSnapshot(eventDup.snapshot);
                        routePaths.push(lastActivatedRoute);
                        routeComponents[lastActivatedRoute] = lastActivatedComponentName;
                    }
                    break;
                case event instanceof NavigationEnd:
                case event instanceof NavigationError:
                case event instanceof NavigationCancel:
                case event instanceof Scroll && event.routerEvent instanceof NavigationEnd:
                    // eslint-disable-next-line no-case-declarations
                    const newActiveComponentIndex = routePaths.findIndex(
                        (item) => item === (event as SafeAny).urlAfterRedirects
                    );
                    if (newActiveComponentIndex > -1) {
                        const toRemove = routePaths.splice(newActiveComponentIndex + 1);
                        toRemove.map((toRemoveKey) => delete routeComponents[toRemoveKey]);
                    } else {
                        if ((event as SafeAny).urlAfterRedirects === '/' || routePaths.length) {
                            const toRemoveIndex =
                                routePaths.length - 1 - routePaths.reverse().findIndex((item) => item === '/');
                            if (toRemoveIndex + 1 === routePaths.length) {
                                routePaths = [];
                                routeComponents = {};
                            } else {
                                const toRemove = routePaths.splice(toRemoveIndex);
                                toRemove.map((toRemoveKey) => delete routeComponents[toRemoveKey]);
                            }
                        }
                    }

                    this.domUpdateSchedulerSvc.updatePathAndComponents(routePaths, routeComponents);
                    this.domUpdateSchedulerSvc.updateActiveComponent();
                    if (this.isAppLoading()) {
                        this.isAppLoading.set(false);
                    }
                    break;
            }
        });

        this.isModernBrowser.set(this.detectIfModernBrowser());
    }

    // TODO: investigate why this is needed (probably because of some 3rd party lib) and remove if possible
    public ngAfterViewChecked(): void {
        this.cdr.detectChanges();
    }

    public ngOnInit(): void {
        this.sfxCommonsLocalizerSvc.setDateFormatsPerLanguage(DATE_FORMATS_PER_LANGUAGE);
        this.sfxCommonsLocalizerSvc.setDateMonthFormatsPerLanguage(DATE_MONTH_FORMATS_PER_LANGUAGE);
    }

    public async disabledDiagnostics(): Promise<void> {
        this.diagnosticsSvc.disable();
        this.areDiagnosticsEnabled.set(this.diagnosticsSvc.tryActivateService(null));
        const queryParams = { ...this.activatedRoute.snapshot.queryParams };
        if (queryParams[this.diagnosticsQueryParamName]) {
            delete queryParams[this.diagnosticsQueryParamName];
            await this.router.navigate([], { queryParams: queryParams });
        }
    }

    private detectIfModernBrowser(): boolean {
        const ua = window.navigator.userAgent;

        const msie = ua.indexOf('MSIE ');
        if (msie > 0) {
            return false;
        }

        const trident = ua.indexOf('Trident/');
        if (trident > 0) {
            return false;
        }

        return true;
    }
}
